import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

type UploadMode = 'file' | 'folder';

interface FileShareItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  sizeBytes: number;
  uploaderId: string;
  uploaderName: string;
  createdAt: string;
  expiresAt: string;
}

function formatBytes(sizeBytes: number): string {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let current = sizeBytes / 1024;
  let idx = 0;
  while (current >= 1024 && idx < units.length - 1) {
    current /= 1024;
    idx += 1;
  }
  return `${current.toFixed(1)} ${units[idx]}`;
}

function formatRemainingTime(expiresAt: string): string {
  const deltaMs = new Date(expiresAt).getTime() - Date.now();
  if (deltaMs <= 0) return 'Đã hết hạn';

  const totalHours = Math.floor(deltaMs / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return `${days} ngày ${hours} giờ`;
}

export const FileSharePage: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<FileShareItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [uploadMode, setUploadMode] = useState<UploadMode>('file');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const authHeaders = useMemo(
    () => ({} as Record<string, string>),
    [user?.id]
  );

  const loadItems = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await fetch('/api/file-share/items', {
        headers: authHeaders
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Không thể tải dữ liệu');
      }
      setItems(data.data ?? []);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Đã có lỗi xảy ra');
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!fileInputRef.current) return;

    if (uploadMode === 'folder') {
      fileInputRef.current.setAttribute('webkitdirectory', '');
      fileInputRef.current.setAttribute('directory', '');
    } else {
      fileInputRef.current.removeAttribute('webkitdirectory');
      fileInputRef.current.removeAttribute('directory');
    }
  }, [uploadMode]);

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setError('Vui lòng chọn file hoặc folder trước khi tải lên');
      return;
    }

    const formData = new FormData();
    formData.append('uploadType', uploadMode);

    if (uploadMode === 'folder') {
      const firstFile = selectedFiles[0] as File & { webkitRelativePath?: string };
      const rootFolderName = firstFile.webkitRelativePath?.split('/')[0] || 'folder';
      formData.append('folderName', rootFolderName);
    }

    Array.from(selectedFiles).forEach((file) => {
      const fileWithPath = file as File & { webkitRelativePath?: string };
      const filename = uploadMode === 'folder' && fileWithPath.webkitRelativePath
        ? fileWithPath.webkitRelativePath
        : file.name;
      formData.append('files', file, filename);
    });

    try {
      setIsLoading(true);
      setError('');
      const response = await fetch('/api/file-share/upload', {
        method: 'POST',
        headers: authHeaders,
        body: formData
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Upload thất bại');
      }

      setSelectedFiles(null);
      await loadItems();
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Upload thất bại');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (item: FileShareItem) => {
    try {
      setError('');
      const endpoint = item.type === 'folder'
        ? `/api/file-share/download-folder/${item.id}`
        : `/api/file-share/download/${item.id}`;

      const response = await fetch(endpoint, { headers: authHeaders });
      if (!response.ok) {
        const failData = await response.json().catch(() => null);
        throw new Error(failData?.error || 'Không thể tải xuống');
      }

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = item.type === 'folder' ? `${item.name}.zip` : item.name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : 'Download thất bại');
    }
  };

  const handleDelete = async (item: FileShareItem) => {
    const confirmed = window.confirm(`Xóa "${item.name}"?`);
    if (!confirmed) return;

    try {
      setIsLoading(true);
      setError('');
      const response = await fetch(`/api/file-share/items/${item.id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Không thể xóa mục');
      }
      await loadItems();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Xóa thất bại');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <h2>Kho chia sẻ file nội bộ (admin)</h2>
      <p style={{ marginBottom: '16px' }}>
        File/folder lưu tối đa 30 ngày, chỉ admin đã đăng nhập mới truy cập được.
      </p>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
        <button
          type="button"
          onClick={() => setUploadMode('file')}
          style={{ fontWeight: uploadMode === 'file' ? 700 : 400 }}
        >
          Upload file
        </button>
        <button
          type="button"
          onClick={() => setUploadMode('folder')}
          style={{ fontWeight: uploadMode === 'folder' ? 700 : 400 }}
        >
          Upload folder
        </button>
      </div>

      <input
        ref={fileInputRef}
        key={uploadMode}
        type="file"
        multiple
        onChange={(event) => setSelectedFiles(event.target.files)}
      />
      <button type="button" onClick={handleUpload} disabled={isLoading} style={{ marginLeft: '10px' }}>
        {isLoading ? 'Đang xử lý...' : 'Tải lên'}
      </button>
      <button type="button" onClick={() => void loadItems()} disabled={isLoading} style={{ marginLeft: '8px' }}>
        Làm mới
      </button>

      {error && <p style={{ color: '#c62828', marginTop: '10px' }}>{error}</p>}

      <div style={{ marginTop: '20px' }}>
        <h3>Danh sách hiện có</h3>
        {items.length === 0 && !isLoading ? (
          <p>Chưa có file/folder nào.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px' }}>Tên</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px' }}>Loại</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px' }}>Dung lượng</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px' }}>Người up</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px' }}>Còn lại</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px' }}>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{item.name}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{item.type}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{formatBytes(item.sizeBytes)}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{item.uploaderName}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>{formatRemainingTime(item.expiresAt)}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                    <button type="button" onClick={() => void handleDownload(item)}>
                      Download
                    </button>
                    <button type="button" onClick={() => void handleDelete(item)} style={{ marginLeft: '8px' }}>
                      Xóa
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
